import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Phone, Minus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePhoneCall } from './PhoneCallProvider';
import { PhoneCallTimer } from './PhoneCallTimer';
import { useContactOptions } from '@/hooks/useContactOptions';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { formatPhoneNumber } from '@/lib/utils';
import { queryKeys } from '@/lib/query-keys';
import { CreateActivityDialog } from '@/components/contacts/details/CreateActivityDialog';
import { captureError } from '@/lib/sentry';
import { contactsListAtom, lastListRouteAtom } from '@/lib/atoms';
import { nextTarget } from './nextContact';
import { useWorkspaceSettings } from '@/hooks/useWorkspaceSettings';
import {
  applyNextContact,
  resolveCadenceDays,
  pickChipOptions,
  DEFAULT_CADENCE_OPTIONS,
  type CadenceOption,
} from '@/lib/contact-cadence';
import type { Contact } from '@/types/contact';

interface PhoneCallLoggerProps {
  onClose: () => void;
  onMinimize: () => void;
  isMinimized: boolean;
}

export function PhoneCallLogger({ onClose, onMinimize, isMinimized }: PhoneCallLoggerProps) {
  const { callState, elapsedSeconds } = usePhoneCall();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const listState = useAtomValue(contactsListAtom);
  const lastListRoute = useAtomValue(lastListRouteAtom);
  const { options } = useContactOptions();
  const [contact, setContact] = useState<Contact | null>(null);
  const [notes, setNotes] = useState('');
  const [callOutcome, setCallOutcome] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const { settings } = useWorkspaceSettings();
  // null = the user has not touched the chips this call. Kept distinct from "the resolved
  // cadence" so Done can tell an active pick from silence — silence must not write an override.
  const [pickedDays, setPickedDays] = useState<number | null>(null);
  const [showAllOptions, setShowAllOptions] = useState(false);

  // CRITICAL: reset per-call state when the call target changes.
  //
  // This panel mounts on callState.isOpen, NOT on contactId, and neither startCall() call
  // site guards against being invoked while a call is already open — the panel is non-modal,
  // so every phone link in the app stays clickable. Clicking another contact's number
  // mid-call refetches `contact` without remounting, so without this a chip picked for
  // contact A would be written as an explicit override on contact B. notes/callOutcome had
  // the same pre-existing leak, which also mislabelled the outcome analytics.
  useEffect(() => {
    setPickedDays(null);
    setShowAllOptions(false);
    setNotes('');
    setCallOutcome('');
  }, [callState.contactId]);

  // Fetch contact on mount
  useEffect(() => {
    if (!callState.contactId) return;
    let cancelled = false;

    supabase
      .from('contacts')
      .select('*')
      .eq('id', callState.contactId)
      .single()
      .then(({ data, error }) => {
        if (!cancelled && !error && data) {
          setContact(data as Contact);
        }
      });

    return () => { cancelled = true; };
  }, [callState.contactId]);

  const saveActivity = useCallback(async () => {
    if (!user || !contact) return;
    const startDate = callState.startTime ? new Date(callState.startTime) : new Date();
    const endDate = new Date();
    const { error } = await supabase.from('activities').insert({
      type: 'phone_call',
      title: `Call with ${callState.contactName}`,
      description: notes || null,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      call_outcome: callOutcome || null,
      contact_id: callState.contactId,
      workspace_id: contact.workspace_id,
      user_id: user.id,
      scheduled: false,
      completed: true,
    });
    if (error) throw error;
  }, [user, contact, callState.startTime, callState.contactName, callState.contactId, notes, callOutcome]);

  const workspaceOptions: CadenceOption[] =
    settings?.skip_options && Array.isArray(settings.skip_options) && settings.skip_options.length > 0
      ? settings.skip_options
      : DEFAULT_CADENCE_OPTIONS;

  const resolvedDays = resolveCadenceDays(contact?.next_contact_interval_days);
  // Derived at render, never synced through an effect — the contact fetch resolves after the
  // panel opens, and an effect would overwrite a chip the user picked while it loaded.
  const selectedDays = pickedDays ?? resolvedDays;
  // True when the contact has no explicit override, i.e. the preselected value is the system
  // default rather than something anyone chose for this contact.
  // Requires `contact` to be loaded: while the fetch is in flight every contact looks
  // override-less, which would flash "(default)" on a contact that actually has one.
  const isFollowingDefault = contact != null && contact.next_contact_interval_days == null;
  const chips = useMemo(
    () => (showAllOptions ? workspaceOptions : pickChipOptions(workspaceOptions, selectedDays)),
    [showAllOptions, workspaceOptions, selectedDays],
  );

  const handleDone = async () => {
    setIsSaving(true);
    try {
      await saveActivity();
      // AFTER the insert: update_contact_last_contacted fires on that insert and would
      // overwrite next_contact if we wrote first. Sequential awaits, last write wins.
      //
      // Runs whether or not the chips were touched — the date always moves, so the contact
      // leaves the Daily list until due. It also overrides the trigger's preserve-a-future-
      // date branch: you just spoke to them, so the clock restarts at their cadence.
      // isExplicit is false on silence: the date moves, but the override stays NULL and the
      // contact keeps following the system default.
      let applied: Date | null = null;
      if (contact) {
        applied = await applyNextContact(contact.id, selectedDays, { isExplicit: pickedDays !== null });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
      toast({
        description: applied && pickedDays !== null
          ? `Next contact ${applied.toLocaleDateString()} — saved as this contact's default`
          : 'Call logged successfully',
      });
      onClose();
    } catch (error) {
      console.error('Error saving call activity:', error);
      captureError(error as Error, { feature: 'phone-phone-call-logger-handle-done' });
      toast({ title: 'Error', description: 'Failed to save call activity', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    setIsSaving(true);
    try {
      await saveActivity();
      // Preserve cadence scheduling — identical to handleDone (see its comment):
      // move next_contact and, only on an explicit chip pick, persist the override.
      let applied: Date | null = null;
      if (contact) {
        applied = await applyNextContact(contact.id, selectedDays, { isExplicit: pickedDays !== null });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
      const target = nextTarget(listState.contacts, listState.totalCount, callState.contactId);
      onClose();
      switch (target.kind) {
        case 'advance':
          toast({ description: `Call logged — next: ${target.nextName}` });
          navigate(`/contacts/${target.nextId}`);
          break;
        case 'end':
          toast({ description: "You've reached the end of the list" });
          navigate(lastListRoute);
          break;
        // more-unloaded: last loaded row but more exist — do NOT claim end-of-list.
        // not-found: call wasn't started from the loaded list.
        // Both: keep the original cadence-aware toast and return to the list they came from.
        case 'more-unloaded':
        case 'not-found':
          toast({
            description: applied && pickedDays !== null
              ? `Next contact ${applied.toLocaleDateString()} — saved as this contact's default`
              : 'Call logged successfully',
          });
          navigate(lastListRoute);
          break;
      }
    } catch (error) {
      console.error('Error saving call activity:', error);
      captureError(error as Error, { feature: 'phone-phone-call-logger-handle-next' });
      toast({ title: 'Error', description: 'Failed to save call activity', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  }, []);

  // Minimized state
  if (isMinimized) {
    return (
      <div
        className="fixed bottom-0 left-4 z-50 bg-card border rounded-t-lg shadow-lg cursor-pointer hover:bg-accent/50 transition-colors"
        onClick={onMinimize}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <Phone className="h-4 w-4 text-green-500" />
          <span className="text-sm font-medium truncate max-w-[120px]">
            {callState.contactName}
          </span>
          <PhoneCallTimer elapsedSeconds={elapsedSeconds} className="text-sm font-mono" />
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <>
      <div className="fixed bottom-0 left-4 z-50 w-[380px] bg-card border rounded-t-lg shadow-lg flex flex-col">
        {/* Header: name, phone, window controls */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{callState.contactName}</div>
            <div className="text-xs text-muted-foreground">
              {formatPhoneNumber(callState.phoneNumber)}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMinimize}>
              <Minus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Centered Timer */}
        <div className="flex items-center justify-center px-3 py-3">
          <PhoneCallTimer elapsedSeconds={elapsedSeconds} className="text-2xl font-bold font-mono" />
        </div>

        {/* Call Outcome + Notes */}
        <div className="px-3 py-2 space-y-2 border-t">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Outcome</Label>
            <Select
              value={callOutcome}
              onValueChange={setCallOutcome}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select outcome..." />
              </SelectTrigger>
              <SelectContent>
                {options.call_outcome.map((o) => (
                  <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder="Call notes..."
            className="resize-none text-sm"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">NEXT CONTACT</Label>
            <div className="flex flex-wrap gap-1">
              {chips.map((opt) => (
                <button
                  key={opt.days}
                  type="button"
                  onClick={() => setPickedDays(opt.days)}
                  className={
                    opt.days === selectedDays
                      ? 'rounded-full border border-primary bg-primary/15 px-2 py-0.5 text-xs'
                      : 'rounded-full border border-input px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent'
                  }
                >
                  {opt.label}
                  {/* Distinguishes "nobody ever set this contact" from "someone deliberately
                      chose 30 days". Without it a highlighted chip looks identical either way,
                      which is exactly the distinction this feature exists to surface. */}
                  {opt.days === selectedDays && pickedDays === null && isFollowingDefault && (
                    <span className="ml-1 opacity-60">(default)</span>
                  )}
                </button>
              ))}
              {!showAllOptions && chips.length < workspaceOptions.length && (
                <button
                  type="button"
                  onClick={() => setShowAllOptions(true)}
                  className="rounded-full border border-dashed border-input px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent"
                >
                  More&hellip;
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 px-3 py-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setShowFollowUp(true)}
          >
            Schedule Follow-up
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={handleNext}
            disabled={isSaving}
          >
            Next
          </Button>
          <Button
            size="sm"
            className="text-xs"
            onClick={handleDone}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Done'}
          </Button>
        </div>
      </div>

      {/* Follow-up dialog */}
      {contact && (
        <CreateActivityDialog
          open={showFollowUp}
          onOpenChange={setShowFollowUp}
          contactId={contact.id}
          defaultValues={{
            title: `Follow-up: ${callState.contactName}`,
            type: 'phone_call',
            start_date: tomorrow,
          }}
        />
      )}
    </>
  );
}
